import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import WorkspacePicker from "../workspace-picker/WorkspacePicker.jsx";
import { useSelector, useDispatch } from "react-redux";
import {
  setUser,
  selectUser,
  setSelectedMenu,
  setSelectedMenuId,
  selectSelectedMenu,
  selectSelectedMenuId,
  selectEditingCollection,
  setUIStates,
} from "../../redux/features/appData/";
import { apiSetCurrentWorkspace } from "../../api.js";
import "./CollectionsSidebar.css";
import { MENUS } from "../config/menus";
import { SIDEBAR_IMAGES } from "../config/images";
import ModalAddWorkspace from "../workspace-picker/ModalAddWorkspace";
import config from "environment";
import { toastFail } from "../../config";
import toast from "react-hot-toast";
import useWebSocket from "react-use-websocket";
import {
  WSMessageUpdate,
  WSMessageConnect,
} from "../../websockets/websocketsMessages";
import { selectUIStates } from "../../redux/features/appData/";
import "../../css/Icons.css";
import { getNavStyle } from "./sidebarHelpers";
import { useCoreDataLoader } from "../../hooks/useCoreDataLoader";
import AvatarMenu from "../header/AvatarMenu";
import { apiSetUserShowBanner } from "../../services/UserService";
import { CHROME_STORE_URL } from "../plugins/Plugins";
import { GmailBanner } from "../banners/GmailBanner";

const PATHS = [
  { path: "home", menu: MENUS.HOME },
  { path: "ai", menu: MENUS.AI },
  { path: "settings", menu: MENUS.SETTINGS_PERSONAL },
  { path: "workspaces", menu: MENUS.SETTINGS_WORKSPACES },
];

const showReportCard = config.showReportCard;

export const CollectionsSidebar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { reloadAll } = useCoreDataLoader();

  const storeUser = useSelector(selectUser);
  const selectedMenu = useSelector(selectSelectedMenu);
  const selectedMenuId = useSelector(selectSelectedMenuId);
  const uiStates = useSelector(selectUIStates);
  const currentCollection = useSelector(selectEditingCollection);

  const [workspaces, setWorkspaces] = useState<any>(null);
  const [showNewWorkspace, setShowNewWorkspace] = useState<boolean>(false);

  const { sendMessage, sendJsonMessage, lastJsonMessage } = useWebSocket(
    config.wsRefreshUrl,
    {
      onOpen: () => console.log("[websocket] opened"),
      onClose: (code: any) => console.log("[websocket] closed: ", code),
      shouldReconnect: (closeEvent) => true,
      onError: (event: WebSocketEventMap["error"]) =>
        console.log("[websocket] error: ", event),
    }
  );

  useEffect(() => {
    if (!storeUser) {
      return;
    }

    // get tabs and apps and such
    (async () => {
      // register
      sendJsonMessage({
        workspaceId: storeUser.currentWorkspaceId,
        userId: storeUser.id,
      } as WSMessageConnect);
    })();

    window.addEventListener("keydown", eventKeyDown);

    return () => {
      window.removeEventListener("keydown", eventKeyDown);
    };
  }, []);

  // when we get the storeUser lets make sure we have the right workspace

  useEffect(() => {
    if (selectedMenu === MENUS.UNSELECTED) {
      dispatch(setSelectedMenu(currentMenu()));
    }

    if (!storeUser) {
      return;
    }

    (async () => {
      const currentWorkspaceId = storeUser?.currentWorkspaceId;

      // check if its in the workspaces
      const index = storeUser.workspaces.findIndex(
        (workspace) => workspace.id === currentWorkspaceId
      );

      if (index === -1) {
        const newWorkspaceId = storeUser.workspaces[0].id;
        await setNewWorkspaceId(newWorkspaceId);
      }

      setWorkspaces(storeUser.workspaces);
    })();

    return;
  }, [storeUser]);

  // expecting a refresh from the client
  useEffect(() => {
    console.log("[Sidebar] socket message incoming: ", lastJsonMessage);

    if (lastJsonMessage !== null && storeUser?.id) {
      const response = lastJsonMessage as WSMessageUpdate;

      if (response.workspaceId) {
        // console.log("[Sidebar/ue] got an automated refresh: ", response);
        if (response.excludeUserId !== storeUser.id) {
          reloadAll(storeUser.currentWorkspaceId);
        } else {
          // console.log("[Sidebar/ue] not updating as that way you: ", response);
        }
      } else {
        toast("Invalid automatic refresh", toastFail);
        console.log("[Sidebar/socket] response: ", response);
      }
    }
  }, [lastJsonMessage]);

  const eventKeyDown = (e: any) => {
    // console.log("[eventKeyDown] key: " + e.key);

    const metaKey = e.metaKey;

    // if (metaKey && (e.key === "L" || e.key === "l")) {
    //   dispatch(setAdderTagContext(null));
    //   dispatch(setAdderOpen(true));
    //   e.stopPropagation();
    //   e.preventDefault();
    // }

    // if (e.key === "Escape") {
    //   dispatch(setAdderTagContext(null));
    //   dispatch(setAdderOpen(false));
    // }
  };

  const updateAfterNewWorkspace = () => {
    setShowNewWorkspace(false);
    dispatch(setSelectedMenu(MENUS.ALL));
    dispatch(setSelectedMenuId(undefined));

    navigate("/home");
  };

  const setNewWorkspaceId = async (newWorkspaceId: string) => {
    if (!storeUser) {
      return;
    }
    const newUser = { ...storeUser, currentWorkspaceId: newWorkspaceId };
    await apiSetCurrentWorkspace(storeUser.id, newWorkspaceId);
    dispatch(setUser(newUser));
  };

  const currentMenu = (): string => {
    let menu = MENUS.HOME;

    const parsed = new URL(window.location.href);

    const secondSlash = parsed.pathname.substring(1).indexOf("/");

    const page =
      secondSlash === -1
        ? parsed.pathname.substring(1)
        : parsed.pathname.substring(1, secondSlash);

    const menuIndex = PATHS.findIndex((path: any) => {
      return path.path === page;
    });

    if (menuIndex !== -1) {
      menu = PATHS[menuIndex].menu;
    }

    return menu;
  };

  const getNavIcon = (menuItem: string, id?: string) => {
    let imgFile = "";

    switch (menuItem) {
      case MENUS.ALL:
        imgFile =
          selectedMenu === MENUS.ALL
            ? SIDEBAR_IMAGES.ALL_ON
            : SIDEBAR_IMAGES.ALL_OFF;
        break;
      case MENUS.ASKED:
        imgFile =
          selectedMenu === MENUS.ASKED
            ? SIDEBAR_IMAGES.ASKED_YOURS_ON
            : SIDEBAR_IMAGES.ASKED_YOURS_OFF;
        break;
      case MENUS.ASKED_ALL:
        imgFile =
          selectedMenu === MENUS.ASKED_ALL
            ? SIDEBAR_IMAGES.ASKED_ALL_ON
            : SIDEBAR_IMAGES.ASKED_ALL_OFF;
        break;
      case MENUS.MY_COLLECTIONS:
        imgFile =
          selectedMenu === MENUS.MY_COLLECTIONS
            ? SIDEBAR_IMAGES.MANAGE_ON
            : SIDEBAR_IMAGES.MANAGE_OFF;
        break;
      case MENUS.HOME:
        imgFile =
          selectedMenu === MENUS.HOME
            ? SIDEBAR_IMAGES.HOME_ON
            : SIDEBAR_IMAGES.HOME_OFF;
        break;
      case MENUS.AI:
        imgFile =
          selectedMenu === MENUS.AI
            ? SIDEBAR_IMAGES.AI_ON
            : SIDEBAR_IMAGES.AI_OFF;
        break;
      case MENUS.COLLECTION_ASK:
        imgFile =
          selectedMenu === MENUS.COLLECTION_ASK
            ? SIDEBAR_IMAGES.COLLECTION_ASK_ON
            : SIDEBAR_IMAGES.COLLECTION_ASK_OFF;
        break;
      case MENUS.COLLECTION_RESOURCES:
        imgFile =
          selectedMenu === MENUS.COLLECTION_RESOURCES
            ? SIDEBAR_IMAGES.COLLECTION_RESOURCES_ON
            : SIDEBAR_IMAGES.COLLECTION_RESOURCES_OFF;
        break;
      case MENUS.COLLECTION_ASKED:
        imgFile =
          selectedMenu === MENUS.COLLECTION_ASKED
            ? SIDEBAR_IMAGES.COLLECTION_ASKED_ON
            : SIDEBAR_IMAGES.COLLECTION_ASKED_OFF;
        break;
      case MENUS.COLLECTION_STATS:
        imgFile =
          selectedMenu === MENUS.COLLECTION_STATS
            ? SIDEBAR_IMAGES.COLLECTION_STATS_ON
            : SIDEBAR_IMAGES.COLLECTION_STATS_OFF;
        break;
      case MENUS.SNIPPET_MANAGER:
        imgFile =
          selectedMenu === MENUS.SNIPPET_MANAGER
            ? SIDEBAR_IMAGES.SNIPPET_MANAGER_ON
            : SIDEBAR_IMAGES.SNIPPET_MANAGER_OFF;
        break;
      case MENUS.COLLECTION_REPORT_CARD:
        imgFile =
          selectedMenu === MENUS.COLLECTION_REPORT_CARD
            ? SIDEBAR_IMAGES.COLLECTION_REPORT_CARD_ON
            : SIDEBAR_IMAGES.COLLECTION_REPORT_CARD_OFF;
        break;
      case MENUS.PERSONA_MANAGER:
        imgFile =
          selectedMenu === MENUS.PERSONA_MANAGER
            ? SIDEBAR_IMAGES.PERSONA_MANAGER_ON
            : SIDEBAR_IMAGES.PERSONA_MANAGER_OFF;
        break;
      case MENUS.CONFIGURE:
        imgFile =
          selectedMenu === MENUS.CONFIGURE
            ? SIDEBAR_IMAGES.CONFIGURE_ON
            : SIDEBAR_IMAGES.CONFIGURE_OFF;
        break;
      case MENUS.POLICY_STUDIO:
        imgFile =
          selectedMenu === MENUS.POLICY_STUDIO
            ? SIDEBAR_IMAGES.POLICY_STUDIO_ON
            : SIDEBAR_IMAGES.POLICY_STUDIO_OFF;
        break;
    }

    return "/icons/" + storeUser?.theme + "/" + imgFile;
  };

  const changeWorkspace = (workspaceId: string) => {
    if (workspaceId === "__new") {
      setShowNewWorkspace(true);
    } else {
      // set new workspace ID
      (async () => {
        setNewWorkspaceId(workspaceId);
        dispatch(setSelectedMenu(MENUS.HOME));
        dispatch(setSelectedMenuId(undefined));

        // const ct = await apiGetWorkspaceTargets(workspaceId);
        // console.log("[CollectionSidebar] changeWorkspace - reloading: ", workspaceId);
        reloadAll(workspaceId);

        navigate("/home");
      })();
    }
  };

  const closeSnippetSidebar = () => {
    dispatch(
      setUIStates({
        ...uiStates,
        snippetSidebarOpen: false,
        snippetEdited: false,
      })
    );
  };

  const closeNewWorkspaceModal = () => {
    setShowNewWorkspace(false);
  };

  const handleCloseBanner = async (e: any) => {
    if (!storeUser) {
      return;
    }

    e.stopPropagation();

    const user = await apiSetUserShowBanner({
      userId: storeUser?.id,
      showBanner: false,
    });

    dispatch(setUser({ ...user }));
  };

  // console.log("[Sidebar/main] tags: ", tags);
  // console.log("[Sidebar/main] apps: ", apps);
  // console.log("[Sidebar/main] contributors: ", contributors);
  // console.log("[Sidebar/main] unseen map: ", unseenMap);
  // console.log("[Sidebar/main] members: ", members);
  // console.log("[Sidebar/main] slackChannels: ", slackChannels);
  // console.log("[Sidebar/main] visibleMembers: ", visibleMembers);
  // console.log("[Sidebar/main] muted: ", muted);
  // console.log("[Sidebar/main] visibleApps: ", visibleApps);
  // console.log("[Sidebar/main] visibleChannels: ", visibleChannels);

  return (
    <div id="collections-sidebar">
      {storeUser && workspaces && (
        <WorkspacePicker
          workspaces={workspaces}
          selectedId={storeUser.currentWorkspaceId}
          onChangeCallback={changeWorkspace}
        />
      )}

      <div className="sidebar-section">
        <div className="sidebar-section-title">Team</div>
        <Link
          onClick={() => {
            closeSnippetSidebar();
            dispatch(setSelectedMenu(MENUS.HOME));
          }}
          to={"/home"}
          key="home">
          <div
            className={getNavStyle(selectedMenu, selectedMenuId, MENUS.HOME)}>
            <div className="menu-icon">
              <img src={getNavIcon(MENUS.HOME)} alt="" />
            </div>
            <div className="menu-text">
              <div className="menu-text-label">
                <span>Home</span>
              </div>
            </div>
          </div>
        </Link>
        <p />
        <div className="sidebar-section-title">Questions</div>
        <Link
          onClick={() => {
            closeSnippetSidebar();
            dispatch(setSelectedMenu(MENUS.COLLECTION_ASK));
          }}
          //to={"/collection/" + currentCollection?.id + "/ask"}
          to={"/agent/" + currentCollection?.id}
          key="collection-ask">
          <div
            className={getNavStyle(
              selectedMenu,
              selectedMenuId,
              MENUS.COLLECTION_ASK
            )}>
            {/* <div className="menu-unseen">
              <div className="menu-unseen-marker-blank">&nbsp;</div>
            </div>
            <div className="menu-icon">
              <img src={getNavIcon(MENUS.COLLECTION_ASK)} alt="" />
            </div>
            <div className="menu-text">
              <div className="menu-text-label">
                <span>Ask A Question</span>
              </div>

              <div className="menu-text-badge"></div>
            </div> */}
            <div className="menu-icon">
              <img src={getNavIcon(MENUS.COLLECTION_ASK)} alt="" />
            </div>
            <div className="menu-text">
              <div className="menu-text-label">
                <span>Ask A Question</span>
              </div>
            </div>
          </div>
        </Link>

        <Link
          onClick={() => {
            closeSnippetSidebar();
            dispatch(setSelectedMenu(MENUS.COLLECTION_ASKED));
          }}
          to={"/asked/" + storeUser?.id}
          key="collection-asked-your">
          <div
            className={getNavStyle(
              selectedMenu,
              selectedMenuId,
              MENUS.COLLECTION_ASKED
            )}>
            {/* <div className="menu-unseen">
              <div className="menu-unseen-marker-blank">&nbsp;</div>
            </div>
            <div className="menu-icon">
              <img src={getNavIcon(MENUS.COLLECTION_ASKED)} alt="" />
            </div>
            <div className="menu-text">
              <div className="menu-text-label">
                <span>Recently Asked</span>
              </div>

              <div className="menu-text-badge"></div>
            </div> */}
            <div className="menu-icon">
              <img src={getNavIcon(MENUS.COLLECTION_ASKED)} alt="" />
            </div>
            <div className="menu-text">
              <div className="menu-text-label">
                <span>Recently Asked</span>
              </div>
            </div>
          </div>
        </Link>

        {/* <Link
          onClick={() => {
                      closeSnippetSidebar();
            dispatch(setSelectedMenu(MENUS.ASKED_ALL));
          }}
          to={"/asked/all"}
          key="asked-all">
          <div
            className={getNavStyle(
              selectedMenu,
              selectedMenuId,
              MENUS.ASKED_ALL
            )}>
            <div className="menu-unseen">
              <div className="menu-unseen-marker-blank">&nbsp;</div>
            </div>
            <div className="menu-icon">
              <img src={getNavIcon(MENUS.ASKED_ALL)} alt="" />
            </div>
            <div className="menu-text">
              <span>All Questions</span>
            </div>
            <div className="menu-text-badge"></div>
          </div>
        </Link> */}

        <p />

        <div className="sidebar-section-title">Resources</div>
        <Link
          onClick={() => {
            closeSnippetSidebar();
            dispatch(setSelectedMenu(MENUS.CONFIGURE));
          }}
          to={"/create"}
          key="configure">
          <div
            className={getNavStyle(
              selectedMenu,
              selectedMenuId,
              MENUS.CONFIGURE
            )}>
            {/* <div className="menu-unseen">
              <div className="menu-unseen-marker-blank">&nbsp;</div>
            </div>
            <div className="menu-icon">
              <img src={getNavIcon(MENUS.CONFIGURE)} alt="" />
            </div>
            <div className="menu-text">
              <div className="menu-text-label">
                <span>Add Resources</span>
              </div>

              <div className="menu-text-badge">
                {currentCollection?.linkRecords &&
                  currentCollection.linkRecords.length < 1 && (
                    <div className="collections-sidebar-start-here">Start</div>
                  )}
              </div>
            </div> */}

            <div className="menu-icon">
              <img src={getNavIcon(MENUS.CONFIGURE)} alt="" />
            </div>
            <div className="menu-text">
              <div className="menu-text-label">
                <span>Add Resources</span>
              </div>
            </div>
          </div>
        </Link>

        <Link
          onClick={() => {
            closeSnippetSidebar();
            dispatch(setSelectedMenu(MENUS.COLLECTION_RESOURCES));
          }}
          to={"/collection/" + currentCollection?.id + "/resources"}
          key="collection-resources">
          <div
            className={getNavStyle(
              selectedMenu,
              selectedMenuId,
              MENUS.COLLECTION_RESOURCES
            )}>
            {/* <div className="menu-unseen">
              <div className="menu-unseen-marker-blank">&nbsp;</div>
            </div>
            <div className="menu-icon">
              <img src={getNavIcon(MENUS.COLLECTION_RESOURCES)} alt="" />
            </div>
            <div className="menu-text">
              <div className="menu-text-label">
                <span>Manage Resources</span>
              </div>

              <div className="menu-text-badge"></div>
            </div> */}
            <div className="menu-icon">
              <img src={getNavIcon(MENUS.COLLECTION_RESOURCES)} alt="" />
            </div>
            <div className="menu-text">
              <div className="menu-text-label">
                <span>Manage Resources</span>
              </div>
            </div>
          </div>
        </Link>
        {config.showPersonas && (
          <Link
            onClick={() => {
              closeSnippetSidebar();
              dispatch(setSelectedMenu(MENUS.PERSONA_MANAGER));
            }}
            to={"/personas"}
            key="collection-personas">
            <div
              className={getNavStyle(
                selectedMenu,
                selectedMenuId,
                MENUS.PERSONA_MANAGER
              )}>
              <div className="menu-icon">
                <img src={getNavIcon(MENUS.PERSONA_MANAGER)} alt="" />
              </div>
              <div className="menu-text">
                <div className="menu-text-label">
                  <span>Persona Manager</span>
                </div>
              </div>
            </div>
          </Link>
        )}
        <p />

        <div className="sidebar-section-title">Manage</div>
        <Link
          onClick={() => {
            closeSnippetSidebar();
            dispatch(setSelectedMenu(MENUS.COLLECTION_STATS));
          }}
          to={"/collection/" + currentCollection?.id + "/stats"}
          key="collection-stats">
          <div
            className={getNavStyle(
              selectedMenu,
              selectedMenuId,
              MENUS.COLLECTION_STATS
            )}>
            {/* <div className="menu-unseen">
              <div className="menu-unseen-marker-blank">&nbsp;</div>
            </div>
            <div className="menu-icon">
              <img src={getNavIcon(MENUS.COLLECTION_STATS)} alt="" />
            </div>
            <div className="menu-text">
              <div className="menu-text-label">
                <span>Dashboard</span>
              </div>

              <div className="menu-text-badge"></div>
            </div> */}

            <div className="menu-icon">
              <img src={getNavIcon(MENUS.COLLECTION_STATS)} alt="" />
            </div>
            <div className="menu-text">
              <div className="menu-text-label">
                <span>Dashboard</span>
              </div>
            </div>
          </div>
        </Link>

        <Link
          onClick={() => {
            closeSnippetSidebar();
            dispatch(setSelectedMenu(MENUS.SNIPPET_MANAGER));
          }}
          to={"/snippets/"}
          key="collection-snippets">
          <div
            className={getNavStyle(
              selectedMenu,
              selectedMenuId,
              MENUS.SNIPPET_MANAGER
            )}>
            <div className="menu-icon">
              <img src={getNavIcon(MENUS.SNIPPET_MANAGER)} alt="" />
            </div>
            <div className="menu-text">
              <div className="menu-text-label">
                <span>Quick Reply Manager</span>
              </div>
            </div>
          </div>
        </Link>
        <p />
        {config.showPolicyStudio && (
          <>
            <div className="sidebar-section-title">Tools</div>
            <Link
              onClick={() => {
                closeSnippetSidebar();
                dispatch(setSelectedMenu(MENUS.POLICY_STUDIO));
              }}
              to={"/ps"}
              key="collection-stats">
              <div
                className={getNavStyle(
                  selectedMenu,
                  selectedMenuId,
                  MENUS.POLICY_STUDIO
                )}>
                <div className="menu-icon">
                  <img src={getNavIcon(MENUS.POLICY_STUDIO)} alt="" />
                </div>
                <div className="menu-text">
                  <div className="menu-text-label">
                    <span>Policy Studio</span>
                  </div>
                </div>
              </div>
            </Link>
          </>
        )}

        {showReportCard && false && (
          <Link
            onClick={() => {
              closeSnippetSidebar();
              dispatch(setSelectedMenu(MENUS.COLLECTION_REPORT_CARD));
            }}
            to={"/collection/" + currentCollection?.id + "/report-card"}
            key="collection-report-card">
            <div
              className={getNavStyle(
                selectedMenu,
                selectedMenuId,
                MENUS.COLLECTION_REPORT_CARD
              )}>
              {/* <div className="menu-unseen">
                <div className="menu-unseen-marker-blank">&nbsp;</div>
              </div>
              <div className="menu-icon">
                <img src={getNavIcon(MENUS.COLLECTION_REPORT_CARD)} alt="" />
              </div>
              <div className="menu-text">
                <div className="menu-text-label">
                  <span>Report Card</span>
                </div>

                <div className="menu-text-badge"></div>
              </div> */}

              <div className="menu-icon">
                <img src={getNavIcon(MENUS.COLLECTION_REPORT_CARD)} alt="" />
              </div>
              <div className="menu-text">
                <div className="menu-text-label">
                  <span>Report Card</span>
                </div>
              </div>
            </div>
          </Link>
        )}
      </div>

      {/* {storeUser?.showBanner && (
        <div
          className="collections-sidebar-banner"
          onClick={() => window.open(CHROME_STORE_URL, "__new")}>
          <GmailBanner />
          <div
            className="collections-sidebar-banner-close"
            onClick={handleCloseBanner}>
            <img className="icon-close" />
          </div>
        </div>
      )} */}

      <div className="collections-sidebar-menu">
        <div className="collections-sidebar-menu-inner">
          {storeUser && storeUser.avatarURL && (
            <AvatarMenu image={storeUser.avatarURL} />
          )}
        </div>
      </div>

      {/* all the hidden stuff goes here */}

      {showNewWorkspace && (
        <ModalAddWorkspace
          onClose={closeNewWorkspaceModal}
          isOpen={showNewWorkspace}
          onSuccess={updateAfterNewWorkspace}
        />
      )}
    </div>
  );
};
